import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiSmartphone, FiMapPin, FiClock, FiStar, FiShield, FiDollarSign } from 'react-icons/fi';
// import { useTranslation } from 'react-i18next';

const FeaturesSection = styled.section`
  background-color: ${props => props.theme.colors.light};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 300px;
    height: 300px;
    background: ${props => props.theme.colors.gradient};
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.1;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 200px;
    height: 200px;
    background: ${props => props.theme.colors.gradient};
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.1;
  }
`;

const FeaturesContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.dark};
  margin-bottom: 1.5rem;
  text-align: center;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.gray};
  margin-bottom: 4rem;
  max-width: 800px;
  text-align: center;
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 100px;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-direction: column;
    text-align: center;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:nth-child(even) {
    flex-direction: row-reverse;
    
    @media (max-width: ${props => props.theme.breakpoints.lg}) {
      flex-direction: column;
    }
  }
`;

const FeatureContent = styled.div`
  flex: 1;
  padding: 0 30px;
  text-align: right;
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    margin-bottom: 40px;
  }
`;

const FeatureTitle = styled(motion.h3)`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.dark};
`;

const FeatureDescription = styled(motion.p)`
  font-size: 1.1rem;
  line-height: 1.5;
  color: ${props => props.theme.colors.gray};
  text-align: right;
`;

const FeatureList = styled(motion.ul)`
  list-style: none;
  margin-bottom: 30px;
  padding: 0;
  text-align: right;
`;

const FeatureItem = styled(motion.li)`
  display: flex;
  flex-direction: row-reverse;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 0.5rem;
  font-size: 16px;
  color: ${props => props.theme.colors.dark};
  
  svg {
    margin-left: 10px;
    margin-right: 0;
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    justify-content: flex-end;
  }
`;

const FeatureButton = styled(motion.button)`
  padding: 12px 25px;
  background: #0a174e;
  color: #fff;
  font-weight: 600;
  border-radius: 30px;
  box-shadow: ${props => props.theme.shadows.small};
  border: none;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  width: fit-content;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  &:hover {
    background: #13245c;
    color: #fff;
    box-shadow: ${props => props.theme.shadows.medium};
  }
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 90%;
    min-width: 140px;
    max-width: 320px;
    padding: 12px 0;
    font-size: 1rem;
  }
`;

const FeatureImage = styled(motion.div)`
  flex: 1;
  
  img {
    width: 100%;
    max-width: 500px;
    border-radius: 15px;
    box-shadow: ${props => props.theme.shadows.medium};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    img {
      max-width: 100%;
    }
  }
`;

const Features: React.FC = () => {
  
  
  return (
    <FeaturesSection id="features">
      <FeaturesContainer>
        {/* Easy to Use & Book Row (now first) */}
        <FeatureRow>
          {/* Image column */}
          <FeatureImage
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <img
              src="/images/app-booking.png"
              alt="سهولة الحجز والاستخدام"
              loading="lazy"
            />
          </FeatureImage>
          {/* Text column */}
          <FeatureContent>
            <SectionTitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              سهولة <span style={{ color: '#DA015C' }}>الاستخدام</span> والحجز
            </SectionTitle>
            <FeatureDescription
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              style={{ marginBottom: 24 }}
            >
              احجز مشوارك أو اطلب توصيلة بضغطة زر من أي مكان وفي أي وقت. تطبيق واصل يجعل التنقل سهلاً وسريعًا للجميع.
            </FeatureDescription>
            <FeatureButton as="a" href="#waitlist" style={{ marginTop: 10 }}>
              <span dir="rtl">سجل الآن&nbsp;←</span>
            </FeatureButton>
          </FeatureContent>
        </FeatureRow>
        {/* Ride Experience Row (now second) */}
        <FeatureRow>
          {/* Image column */}
          <FeatureImage
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <img
              src="/images/wassel-driver.jpg"
              alt="سائق واصل"
              loading="lazy"
            />
          </FeatureImage>
          {/* Text column */}
          <FeatureContent>
            <SectionTitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              تجربة ركوب <span style={{ color: '#DA015C' }}>استثنائية</span>
            </SectionTitle>
            <FeatureList
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <FeatureItem>
                <span style={{fontSize:'1.6em',marginLeft:'8px'}}><FiShield /></span>
                أمان وراحة: سائقون محترفون وسيارات مريحة.
              </FeatureItem>
              <FeatureItem>
                <span style={{fontSize:'1.6em',marginLeft:'8px'}}><FiDollarSign /></span>
                أسعار شفافة: تعرف على التكلفة مسبقًا.
              </FeatureItem>
              <FeatureItem>
                <span style={{fontSize:'1.6em',marginLeft:'8px'}}><FiClock /></span>
                دعم على مدار الساعة: فريق متواجد لمساعدتك بأي وقت.
              </FeatureItem>
            </FeatureList>
            <FeatureButton as="a" href="#waitlist" style={{ marginTop: 10 }}>
              <span dir="rtl">انضم إلى واصل&nbsp;←</span>
            </FeatureButton>
            
          </FeatureContent>
        </FeatureRow>
        {/* Real-Time Tracking Row */}
        <FeatureRow>
          {/* Image column */}
          <FeatureImage
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <img
              src="/images/Real-time-Tracking.png"
              alt="تتبع الرحلات والطلبات في الوقت الفعلي"
              loading="lazy"
            />
          </FeatureImage>
          {/* Text column */}
          <FeatureContent>
            <SectionTitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              تتبّع <span style={{ color: '#DA015C', fontWeight: 800 }}>مباشر</span> في الوقت الفعلي
            </SectionTitle>
            <FeatureDescription
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              style={{ marginBottom: 24 }}
            >
              راقب رحلة سيارتك أو توصيل طلبك لحظة بلحظة عبر تطبيق واصل. استمتع براحة البال مع إشعارات فورية ومعلومات دقيقة عن موقع السائق أو المندوب في كل خطوة.
            </FeatureDescription>
            <FeatureButton as="a" href="#waitlist" style={{ marginTop: 10 }}>
              <span dir="rtl">سجل الآن&nbsp;←</span>
            </FeatureButton>
          </FeatureContent>
        </FeatureRow>
      </FeaturesContainer>
    </FeaturesSection>
  );
};

export default Features;